/**
 * Helpers puros para detección y resolución de la ciudad del viaje (mapTripCity).
 * Módulo puro JS sin dependencias de React Native (testeable en Node/CI).
 */

/**
 * Resuelve la ciudad del viaje de forma síncrona / instantánea:
 * 1. Intenta por dirección de recogida (pickupHint).
 * 2. Intenta por coordenadas de recogida o GPS (pickupLat/Lng ?? gpsLat/Lng).
 * 3. Si ya hay una ciudad detectada válida en pricingCities, la conserva.
 * 4. Fallback a la ciudad configurada en el perfil del usuario.
 *
 * @param {Object} params
 * @param {string} [params.pickupHint] - Dirección de recogida
 * @param {number|null} [params.lat] - Latitud de pickup o GPS
 * @param {number|null} [params.lng] - Longitud de pickup o GPS
 * @param {string|null} [params.currentTripCityId] - ID de ciudad actualmente guardado en estado
 * @param {Array} [params.pricingCities] - Ciudades disponibles con tarifas
 * @param {Array} [params.zonificacionCities] - Ciudades completas de zonificación
 * @param {string|null} [params.userProfileCityId] - cityId del usuario logueado
 * @param {Function} [params.matchPricingCityByHint] - Función de búsqueda por hint
 * @param {Function} [params.matchPricingCityByCoords] - Función de búsqueda por coords
 * @param {Function} [params.resolveTripCityId] - Función de resolución final
 * @returns {{ cityId: string|null, detectedCity: Object|null, source: string }}
 */
export function resolveInstantTripCity({
  pickupHint = '',
  lat = null,
  lng = null,
  currentTripCityId = null,
  pricingCities = [],
  zonificacionCities = [],
  userProfileCityId = null,
  matchPricingCityByHint = null,
  matchPricingCityByCoords = null,
  resolveTripCityId = null,
} = {}) {
  const fromHint = pickupHint && typeof matchPricingCityByHint === 'function'
    ? matchPricingCityByHint(zonificacionCities, pickupHint)
    : null;

  const validCoords =
    lat != null &&
    lng != null &&
    Number.isFinite(Number(lat)) &&
    Number.isFinite(Number(lng));

  const fromCoords = validCoords && typeof matchPricingCityByCoords === 'function'
    ? matchPricingCityByCoords(zonificacionCities, Number(lat), Number(lng))
    : null;

  const detected = fromHint || fromCoords;
  if (detected?.id) {
    return {
      cityId: detected.id,
      detectedCity: detected,
      source: fromHint ? 'pickup_hint' : 'coords',
    };
  }

  if (currentTripCityId && (pricingCities || []).some((c) => c && c.id === currentTripCityId)) {
    return {
      cityId: currentTripCityId,
      detectedCity: (pricingCities || []).find((c) => c && c.id === currentTripCityId) || null,
      source: 'current_selection',
    };
  }

  const fallbackId = typeof resolveTripCityId === 'function'
    ? resolveTripCityId(zonificacionCities, null, userProfileCityId)
    : null;

  return {
    cityId: fallbackId || null,
    detectedCity: null,
    source: fallbackId ? 'user_profile' : 'none',
  };
}

/**
 * Extrae y busca coincidencias en una lista de lugares obtenidos por geocodificación inversa.
 *
 * @param {Array} places - Lugares de reverseGeocodeAsync
 * @param {Array} zonificacionCities - Ciudades de zonificación
 * @param {Function} matchPricingCityByHint - Función de búsqueda por hint
 * @returns {Object|null} Ciudad encontrada o null
 */
export function matchCityFromReverseGeocode(places, zonificacionCities, matchPricingCityByHint) {
  if (!Array.isArray(places) || !places.length || typeof matchPricingCityByHint !== 'function') {
    return null;
  }
  const p = places[0];
  if (!p) return null;

  const hints = [p.city, p.subregion, p.district, p.region, p.name].filter(Boolean);
  for (let i = 0; i < hints.length; i++) {
    const matched = matchPricingCityByHint(zonificacionCities, hints[i]);
    if (matched) return matched;
  }

  const composed = [p.city, p.subregion, p.region].filter(Boolean).join(' ');
  if (composed) {
    const matchedAddr = matchPricingCityByHint(zonificacionCities, composed);
    if (matchedAddr) return matchedAddr;
  }

  return null;
}

/**
 * Resuelve la ciudad a partir del perfil del usuario (cityId o cityName) como fallback.
 *
 * @param {Object} profile - auth.profile
 * @param {Array} pricingCities - Lista de ciudades con precios/tarifas
 * @param {Array} zonificacionCities - Ciudades de zonificación
 * @param {Function} matchPricingCityByHint - Función de búsqueda por hint
 * @returns {Object|null} Ciudad encontrada o null
 */
export function matchCityFromProfile(profile, pricingCities = [], zonificacionCities = [], matchPricingCityByHint = null) {
  if (!profile) return null;

  if (profile.cityId && pricingCities.some((c) => c && c.id === profile.cityId)) {
    return pricingCities.find((c) => c && c.id === profile.cityId) || null;
  }

  if (profile.cityName && typeof matchPricingCityByHint === 'function') {
    return matchPricingCityByHint(zonificacionCities, profile.cityName) || null;
  }

  return null;
}
